// VideoStudy/studySetVideos.ts
//
// A study set as a video playlist (ORA-681).
//
// The videos slice is keyed by "container": a lecture id for lecture videos, a
// study session id for the daily session, `topic:<id>` for a topic. A set is
// one more container, so the runner reads its playlist exactly the way it reads
// every other one and nothing downstream has to know where these videos came
// from.

import type {
  ChatStudySetBlockPayload,
  ChatStudySetItem,
} from '@/types/chatBlocks';

/** The videos-slice container a set's playlist lives in. */
export const studySetVideoContainerId = (setId: string) => `set:${setId}`;

/** A set's video items, in the order the server put them in. */
const orderedVideoItems = (
  set: Pick<ChatStudySetBlockPayload, 'items'>
): Extract<ChatStudySetItem, { kind: 'video' }>[] =>
  (set.items ?? [])
    .filter(
      (item): item is Extract<ChatStudySetItem, { kind: 'video' }> =>
        item.kind === 'video'
    )
    .sort((left, right) => left.ord - right.ord);

/**
 * The videos a set names, in the order the server put them in.
 *
 * A drawn set carries its items whatever its size — the chat block hides them
 * for a lazy set, but `GET /:id` and the launch and return responses send them
 * so a playlist can be built — and a set that has never been drawn carries
 * none, in which case the runner has nothing to play and says so rather than
 * falling back to the daily session's videos.
 */
export const studySetVideoIds = (
  set: Pick<ChatStudySetBlockPayload, 'items'>
): string[] => orderedVideoItems(set).map((item) => item.video.videoId);

/**
 * Which of a set's videos the server already counts as done, by video id.
 *
 * The set's own `done` flags, not the global watch state: a student who
 * watched one of these videos last month has not watched it *for this set*,
 * and a playlist that reports itself finished the moment it opens is no
 * playlist at all.
 */
export const studySetVideoDoneById = (
  set: Pick<ChatStudySetBlockPayload, 'items'>
): Record<string, boolean> =>
  Object.fromEntries(
    orderedVideoItems(set).map((item) => [item.video.videoId, item.done])
  );

/**
 * Whether every video in a set has been watched — the set's own record, plus
 * the ones completed in this run since it opened.
 *
 * A set whose items have not been read back yet is not finished: an unknown
 * playlist must never end the session.
 */
export const isStudySetPlaylistFinished = ({
  doneById,
  doneHereVideoIds,
}: {
  doneById: Record<string, boolean> | null;
  doneHereVideoIds: ReadonlySet<string>;
}): boolean => {
  if (!doneById) return false;

  const videoIds = Object.keys(doneById);
  if (videoIds.length === 0) return false;

  return videoIds.every(
    (videoId) => doneById[videoId] || doneHereVideoIds.has(videoId)
  );
};
